import React, { useEffect, useRef, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Star, ExternalLink, Trash2, Loader2, Search, ShieldAlert, MessageSquareText, ScanSearch, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const SOURCE_LABELS = { trustpilot: "Trustpilot", instagram: "Instagram", facebook: "Facebook" };
const SOURCE_PLACEHOLDER = {
  trustpilot: "https://de.trustpilot.com/review/eurefirma.de",
  instagram: "instagram.com/handle  oder  handle",
  facebook: "https://www.facebook.com/EureSeite",
};
const SEVERITY = {
  schwer: { label: "Schwer", cls: "bg-rose-50 text-rose-700 border border-rose-200" },
  mittel: { label: "Mittel", cls: "bg-orange-50 text-orange-700 border border-orange-200" },
  leicht: { label: "Leicht", cls: "bg-amber-50 text-amber-700 border border-amber-200" },
  keiner: { label: "Kein Verstoß", cls: "bg-[#F4F1EC] text-[#8B8A8E] border border-[#E8E4DF]" },
};

export default function Mentions() {
  const [source, setSource] = useState("instagram");
  const [target, setTarget] = useState("");
  const [job, setJob] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [logoCheckId, setLogoCheckId] = useState(null);
  const pollRef = useRef(null);

  const loadMentions = async () => {
    const params = filter === "all" ? {} : filter === "violations" ? { only_violations: true } : { source: filter };
    const r = await api.get("/mentions", { params });
    setMentions(r.data);
  };

  useEffect(() => { loadMentions(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/mentions/jobs/${jobId}`);
        setJob(r.data);
        if (["PROCESSED", "FAILED"].includes(r.data.status)) {
          clearInterval(pollRef.current);
          setLoading(false);
          if (r.data.status === "PROCESSED") {
            toast.success(`${r.data.found} Erwähnung(en) gefunden.`);
            if (r.data.found === 0 && r.data.error) toast.message(r.data.error);
          } else {
            toast.error(r.data.error || "Scan fehlgeschlagen.");
          }
          loadMentions();
        }
      } catch {
        clearInterval(pollRef.current);
        setLoading(false);
      }
    }, 4000);
  };

  const startScan = async () => {
    if (!target.trim()) return toast.error("Bitte eine URL bzw. einen Instagram-Namen angeben.");
    setLoading(true);
    setJob(null);
    try {
      const r = await api.post("/mentions/scan", { source, target: target.trim() });
      setJob(r.data);
      poll(r.data.id);
      toast.success("Scan gestartet – Daten werden geladen …");
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.detail || "Start fehlgeschlagen.");
    }
  };

  const remove = async (id) => {
    await api.delete(`/mentions/${id}`);
    toast.success("Erwähnung gelöscht.");
    loadMentions();
  };

  const checkLogo = async (m) => {
    setLogoCheckId(m.id);
    try {
      const r = await api.post("/logo/check", { image_url: m.image_url, mention_id: m.id });
      if (!r.data.contains_logo) toast.message("Kein STIG-ROCK-Logo erkannt.");
      else if (r.data.is_exact === false || r.data.background_violation) toast.error("Logo-Verstoß erkannt!");
      else toast.success("Logo korrekt verwendet.");
      loadMentions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logo-Prüfung fehlgeschlagen.");
    } finally {
      setLogoCheckId(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="mentions-page">
      <PageHeader title="Erwähnungen & Bewertungen" subtitle="Trustpilot-Bewertungen sowie Instagram-/Facebook-Profile per Apify abrufen und KI-bewerten" />

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">Quelle</label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger data-testid="mention-source" className="w-full rounded-md border-[#E0DCD6] sm:w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">Ziel (URL / Name)</label>
            <Input
              data-testid="mention-target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !loading && startScan()}
              placeholder={SOURCE_PLACEHOLDER[source]}
              className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]"
            />
          </div>
          <Button
            data-testid="mention-start"
            onClick={startScan}
            disabled={loading}
            className="rounded-md bg-[#0A0A0A] px-5 text-white hover:bg-[#262626]"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            {loading ? "Lädt …" : "Abrufen"}
          </Button>
        </div>

        {job && (
          <div data-testid="mention-job-status" className="mt-4 flex items-center gap-2 rounded-md bg-[#F4F1EC] px-4 py-3 text-sm text-[#3A3A3C]">
            {["RUNNING", "READY"].includes(job.status) && <Loader2 className="h-4 w-4 animate-spin" />}
            <span className="font-semibold">{SOURCE_LABELS[job.source]}</span>
            <span className="text-[#8B8A8E]">·</span>
            <span className="truncate">{job.target}</span>
            <span className="text-[#8B8A8E]">·</span>
            <span>{job.status === "PROCESSED" ? `${job.found} Treffer` : job.status === "FAILED" ? "Fehlgeschlagen" : "läuft …"}</span>
          </div>
        )}
        <p className="mt-3 text-xs text-[#8B8A8E]">
          Max. 50 Einträge pro Lauf (Kostenkontrolle). Der Abruf läuft im Hintergrund und kann einige Sekunden dauern.
        </p>
      </Card>

      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Ergebnisse</h3>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="mention-filter" className="w-48 rounded-md border-[#E0DCD6]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="violations">Nur Verstöße</SelectItem>
            <SelectItem value="trustpilot">Trustpilot</SelectItem>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="facebook">Facebook</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {mentions.length === 0 ? (
        <Card className="rounded-lg border-[#E8E4DF] bg-white p-12 text-center shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
          <MessageSquareText className="mx-auto h-8 w-8 text-[#A6A4A9]" />
          <p className="mt-2 text-sm text-[#8B8A8E]">Noch keine Erwähnungen. Starten Sie oben einen Abruf.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {mentions.map((m) => {
            const sev = SEVERITY[m.ai_severity] || SEVERITY.keiner;
            return (
              <Card key={m.id} data-testid={`mention-${m.id}`}
                className="rounded-lg border-[#E8E4DF] bg-white p-5 shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-sm bg-[#0A0A0A] px-2.5 py-1 text-xs font-semibold text-white">{SOURCE_LABELS[m.source]}</span>
                  {m.ai_is_violation && <ShieldAlert className="h-4 w-4 text-rose-600" />}
                  <span className={`rounded-sm px-2.5 py-1 text-xs font-semibold ${sev.cls}`}>{sev.label}</span>
                  {typeof m.rating === "number" && (
                    <span className="inline-flex items-center gap-1 rounded-sm border border-[#E8E4DF] bg-[#FAF7F2] px-2.5 py-1 text-xs font-semibold text-[#0A0A0A]">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {m.rating}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-[#8B8A8E]">{formatDateTime(m.created_at)}</span>
                </div>

                <div className="mt-3 flex items-start gap-3">
                  {m.image_url && (
                    <img src={m.image_url} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" referrerPolicy="no-referrer"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  )}
                  <div className="min-w-0 flex-1">
                    {(m.title || m.author) && (
                      <p className="font-semibold text-[#0A0A0A]">{m.title || m.author}</p>
                    )}
                    {m.author && m.title && m.author !== m.title && (
                      <p className="text-xs text-[#8B8A8E]">{m.author}</p>
                    )}
                    {m.text && <p className="mt-1 text-sm text-[#3A3A3C]">{m.text}</p>}
                  </div>
                </div>

                {m.ai_reason && (
                  <p className="mt-3 rounded-md bg-[#F4F1EC] p-3 text-xs text-[#3A3A3C]">
                    <span className="font-semibold">KI ({m.ai_confidence}%):</span> {m.ai_reason}
                  </p>
                )}

                {m.logo_checked && (
                  <p data-testid={`logo-result-${m.id}`} className={`mt-2 flex items-start gap-1.5 rounded-md p-3 text-xs ${
                    m.logo_detected && (m.logo_exact === false) ? "bg-rose-50 text-rose-700" :
                    m.logo_detected ? "bg-emerald-50 text-emerald-700" : "bg-[#F4F1EC] text-[#6B6A6E]"
                  }`}>
                    {m.logo_detected
                      ? (m.logo_exact === false ? <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />)
                      : <ScanSearch className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                    <span>
                      <span className="font-semibold">
                        {m.logo_detected ? (m.logo_exact === false ? "Logo abgewandelt!" : "Logo korrekt") : "Kein Logo erkannt"}
                      </span>{m.logo_note ? ` – ${m.logo_note}` : ""}
                    </span>
                  </p>
                )}

                <div className="mt-3 flex items-center gap-3">
                  {m.url && (
                    <a href={m.url} target="_blank" rel="noopener noreferrer" data-testid={`mention-link-${m.id}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-[#0A0A0A] underline decoration-[#D8D3CC] underline-offset-2 hover:text-[#A6A4A9]">
                      <ExternalLink className="h-3.5 w-3.5" /> Öffnen
                    </a>
                  )}
                  {m.image_url && (
                    <Button data-testid={`logo-check-${m.id}`} variant="ghost" size="sm"
                      onClick={() => checkLogo(m)} disabled={logoCheckId === m.id}
                      className="h-8 rounded-md text-[#0A0A0A] hover:bg-[#F4F1EC]">
                      {logoCheckId === m.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ScanSearch className="mr-1.5 h-3.5 w-3.5" />}
                      Logo prüfen
                    </Button>
                  )}
                  <Button data-testid={`mention-delete-${m.id}`} variant="ghost" size="sm"
                    onClick={() => remove(m.id)}
                    className="ml-auto h-8 rounded-md text-[#A6A4A9] hover:bg-rose-50 hover:text-rose-600">
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Löschen
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
